// Der Fahrplan (Konzept §4). Kein mitgelieferter Fahrplan für Spieler — aber
// natürlich ein objektiv existierender, weil er sonst nicht entschlüsselbar
// wäre. Er ist deterministisch aus der Weltzeit ableitbar: Server und Client
// rechnen dieselbe Zugposition, ohne dass der Server jeden Meter schicken muss.
//
// ============================================================================
// Zwei Züge, eine eingleisige Strecke (Konzept §10.1 / §12.1 / §16 Frage 3)
// ============================================================================
//
//   LOCAL  — der hoppbare Pendler (Süd↔Nord / Mine). Mehrperioden-Rätsel.
//   THROUGH — durchfahrender Güterzug. Hält kurz am Hof, nicht zum Aufspringen
//             gedacht (boardable: false). Erzeugt *Ereignisse* am Südhof,
//             während LOCAL auf der Mine oder am Nordende ist — damit Warten
//             zu Beobachten wird (Konzept §16 Frage 3), ohne den Hof zum Zoo
//             zu machen.
//
// Begegnungen: Absolute Blockfreiheit auf dem offenen Korridor. THROUGH fährt
// nur, wenn LOCAL den Korridor freigibt (steht im Hof / auf der Nebenstrecke).
// In den Höfen stehen sie auf getrennten Standplätzen (Nebengleis-Logik,
// STATIONS[].sidings) — Kopfabstand ≥ MIN_YARD_HEAD_SEP.
//
// ============================================================================
// LOCAL — Mehrperioden-Struktur (Konzept §4: der Fahrplan ist der Endgegner)
// ============================================================================
//
//   P1 GRUNDTAKT
//     Pendel Süd ↔ Nord. Standzeiten aus Seed(run, station), Kurz/Lang-Muster
//     (≈82 % kurz 20–70 Weltmin, 18 % lang 2–4 Welth).
//
//   P2 NACHTLAUF
//     Weltzeit 22:00–05:00: Standzeiten ×1,55. Erste Süd-Standzeit nach 05:00
//     verkürzt (Frühausfahrt).
//
//   P3 NEBENSTRECKEN-UMLAUF (Konzept §2.1 Sackgasse)
//     (runIndex % SPUR_EVERY) === SPUR_PHASE → Mine statt Nord.
//     SPUR_EVERY = 7, SPUR_PHASE = 3 — abzählbar, nicht Rauschen.
//
//   P4 LANGTAG
//     Alle LANG_EVERY = 21 Umläufe: extra-lange Süd-Standzeit.
//
// ============================================================================
// THROUGH — erlernbarer Gegentakt
// ============================================================================
//   Pendelt Nord-Ausweich ↔ Süd-Ausweich, sobald der Korridor frei ist.
//   Kurzer Südhalt (8–18 Weltmin ≈ 40–90 Echtsekunden): sichtbar, hörbar,
//   aber nicht „der Zug, auf den man wartet". Muster: „wenn der Pendler weg
//   ist, kommt oft der Durchfahrer" — aus Beobachtung ableitbar, kein RNG.
//
// Beobachtungsdauer LOCAL bleibt Stunden–Tage (check-network). Verkehrsdichte
// am Südhof steuert die *gefühlte* 15-Minuten-Session (Prototyp 1 / §17).

import {
  TRACK_LENGTH, STATIONS, DEAD_END, JUNCTION,
  BRANCH_MAIN, BRANCH_MINE, MINE_TRACK_LENGTH,
  trackLength, trackPoint,
} from './track.js';
import { makeRng, rngRange, hashInt } from './rng.js';
import { GAME_SECONDS_PER_DAY, TIME_SCALE } from './constants.js';
import { secondsIntoDay } from './worldtime.js';
import {
  buildConsist, CONSIST_PLAN, THROUGH_CONSIST_PLAN,
} from './consist.js';

const SOUTH = STATIONS[0];
const NORTH = STATIONS[1];
const MINE = DEAD_END;

// Wo LOCAL im Hof steht (Spitze, Bogenlänge).
export const SOUTH_STAND_S = SOUTH.s;
export const NORTH_STAND_S = NORTH.s;
export const MINE_STAND_S = MINE.s;
export const JUNCTION_S = JUNCTION.fromS;

// THROUGH-Standplätze.
// Süd: Nebengleis im Hof (sichtbar, getrennt von LOCAL).
// „Nord"-Halt ist bewusst ein *Mittelhalt* nahe der Weiche — nicht Mott.
// Begründung: volle Süd↔Nord-Fahrten dauern ~2 Welthunden und passen nicht
// in kurze LOCAL-Standfenster; der kurze Pendel Süd↔Weiche füllt genau die
// Lücken, in denen LOCAL auf der Mine oder am Nordende ist (Beobachten).
export const SOUTH_THROUGH_S = 520;
export const NORTH_THROUGH_S = 2100; // Mittelhalt / Begegnungsabschnitt bei Weiche

// --- Zug-IDs / Metadaten ----------------------------------------------------

export const TRAIN_LOCAL = 'local';
export const TRAIN_THROUGH = 'through';
export const TRAIN_IDS = Object.freeze([TRAIN_LOCAL, TRAIN_THROUGH]);

// Zuglängen aus der gebauten Komposition — nicht handgeschrieben.
// Früher standen hier 182 / 130; THROUGH war real 155,4 m (25 m zu kurz).
// Die falsche Zahl steckte in trainsConflict / Belegung und erlaubte
// Begegnungen, die geometrisch ineinander liefen. Ableitung aus buildConsist
// hält Fahrplan und Waggonbau per Konstruktion synchron (kein Modul-Zyklus:
// consist.js importiert schedule.js nicht).
const _localConsist = buildConsist(CONSIST_PLAN);
const _throughConsist = buildConsist(THROUGH_CONSIST_PLAN);
export const LOCAL_TRAIN_LENGTH = _localConsist.totalLength;
export const THROUGH_TRAIN_LENGTH = _throughConsist.totalLength;

/** Mindestabstand der Zugköpfe im Hof (parallele Gleise / Ausweichen). */
export const MIN_YARD_HEAD_SEP = 200;
/**
 * Mindestlücke zwischen Zugenden auf demselben Gleis (offen).
 * Früher 90 m — mit echten Kompositionslängen und gleichgerichteten
 * Begegnungen knappe Kanten (~3 m über dem Limit). 140 m ≈ ein Güterwagen
 * extra Puffer, damit der Planner nicht auf Millimeter plant und
 * numerische Trapez-Samples nicht die Lücke fressen.
 */
export const MIN_OPEN_GAP_M = 140;

/**
 * Sicht- und Hörweiten vom Beobachter aus (Weltmeter, euklidisch).
 * Fog far in sky.js = 800 — Silhouette bei 500 m noch im Nebel, nicht culled.
 */
export const TRAIN_SIGHT_M = 400;
export const TRAIN_HORN_RANGE_M = 900;
export const TRAIN_RUMBLE_RANGE_M = 1400;
export const TRAIN_SING_RANGE_M = 700;

/** Vorlauf, in dem Arbeiter am Hof „Abfahrt vorbereiten" (Weltsekunden). */
export const PRE_DEPART_WORLD_S = 8 * 60; // 8 Weltmin ≈ 40 Echtsekunden

export const TRAINS = Object.freeze([
  {
    id: TRAIN_LOCAL,
    role: 'local',
    boardable: true,
    length: LOCAL_TRAIN_LENGTH,
    // train.js: bestehende Komposition (buildConsist).
    consistHint: 'default',
  },
  {
    id: TRAIN_THROUGH,
    role: 'through',
    boardable: false,
    length: THROUGH_TRAIN_LENGTH,
    // train.js muss eine zweite, kürzere Komposition bauen (siehe Bericht).
    consistHint: 'through-freight',
  },
]);

// --- Nebenstrecken-Muster (erlernbar) ---------------------------------------
/** Jeder SPUR_EVERY-te Umlauf ab SPUR_PHASE bedient die Sackgasse. */
export const SPUR_EVERY = 7;
export const SPUR_PHASE = 3;
/** Extra-langer Südhalt alle LANG_EVERY Umläufe (Vielfaches von SPUR_EVERY). */
export const LANG_EVERY = 21;

/** true, wenn dieser Umlauf-Index die Nebenstrecke statt Nord bedient. */
export function isSpurRun(runIndex) {
  return ((runIndex % SPUR_EVERY) + SPUR_EVERY) % SPUR_EVERY === SPUR_PHASE;
}

// --- Fahrdynamik -------------------------------------------------------------
// Geschwindigkeiten in Echtzeit-m/s, Umrechnung in Weltzeit: v_welt = v_echt / TIME_SCALE.
// 13,5 m/s ≈ 49 km/h: realistischer Güterzug auf Nebenstrecke.
export const CRUISE_SPEED_REAL = 13.5;
export const ACCEL_REAL = 0.16;
export const BRAKE_REAL = 0.22;
export const YARD_SPEED_REAL = 4.0;

export const CRUISE_SPEED = CRUISE_SPEED_REAL / TIME_SCALE;
export const ACCEL = ACCEL_REAL / (TIME_SCALE * TIME_SCALE);
export const BRAKE = BRAKE_REAL / (TIME_SCALE * TIME_SCALE);
export const YARD_SPEED = YARD_SPEED_REAL / TIME_SCALE;

// Standzeiten LOCAL in Weltsekunden (Grundtakt P1).
// Leicht verdichtet gegenüber der Erstfassung: weniger Extrem-Langhalte, damit
// der Pendler allein schon etwas öfter am Hof ist — der Durchfahrer trägt den
// Rest. Muster (Kurz/Lang/Nacht/Spur/Langtag) bleibt erlernbar.
const DWELL_MIN = 20 * 60;
const DWELL_MAX = 4 * 3600;
const DWELL_LONG_CHANCE = 0.18;

// Spur-Standzeit (Isolation): 9–14 Welthunden ≈ 45–70 Echtzeitminuten.
// Untergrenze 9 h hält check-network / Konzept §2.1 (lange Isolation).
const SPUR_DWELL_MIN = 9 * 3600;
const SPUR_DWELL_MAX = 14 * 3600;

const NIGHT_START = 22 * 3600;
const NIGHT_END = 5 * 3600;
const NIGHT_DWELL_MULT = 1.55;
const MORNING_DWELL_MULT = 0.72;

// THROUGH-Standzeiten (Weltsekunden).
// Süd: lange genug, um *gesehen* zu werden (Beobachtungsgegenstand), kurz
// genug, dass er nicht wie der hoppbare Pendler wirkt (boardable: false).
// 40–100 Weltmin ≈ 3–8 Echtzeitminuten am Hof.
const THROUGH_SOUTH_DWELL_MIN = 40 * 60;
const THROUGH_SOUTH_DWELL_MAX = 100 * 60;
// Mittelhalt: kürzer — wartet auf freie Trasse, dann wieder Richtung Süd.
const THROUGH_NORTH_DWELL_MIN = 12 * 60;
const THROUGH_NORTH_DWELL_MAX = 35 * 60;

/**
 * Trapez-Fahrzeit für eine Distanz (Weltsekunden).
 * direction > 0 = bergauf-Malus (nur sinnvoll auf Hauptstrecke Nord).
 */
function travelTimeForDistance(dist, gradePenalty = 1) {
  const accelTime = CRUISE_SPEED / ACCEL;
  const brakeTime = CRUISE_SPEED / BRAKE;
  const accelDist = 0.5 * ACCEL * accelTime * accelTime;
  const brakeDist = 0.5 * BRAKE * brakeTime * brakeTime;
  const cruiseDist = Math.max(0, dist - accelDist - brakeDist);
  let t = accelTime + brakeTime + cruiseDist / CRUISE_SPEED;
  return t * gradePenalty;
}

const DIST_MAIN = Math.abs(NORTH_STAND_S - SOUTH_STAND_S);
const DIST_SOUTH_TO_JUNC = Math.abs(JUNCTION_S - SOUTH_STAND_S);
const DIST_JUNC_TO_MINE = Math.abs(MINE_STAND_S - 0);
const DIST_THROUGH = Math.abs(NORTH_THROUGH_S - SOUTH_THROUGH_S);

const TRAVEL_NORTH = travelTimeForDistance(DIST_MAIN, 1.18);
const TRAVEL_SOUTH = travelTimeForDistance(DIST_MAIN, 1.0);
const TRAVEL_TO_MINE = travelTimeForDistance(DIST_SOUTH_TO_JUNC, 1.0)
  + travelTimeForDistance(DIST_JUNC_TO_MINE, 1.12);
const TRAVEL_FROM_MINE = travelTimeForDistance(DIST_JUNC_TO_MINE, 1.0)
  + travelTimeForDistance(DIST_SOUTH_TO_JUNC, 1.0);
const TRAVEL_THROUGH_N = travelTimeForDistance(DIST_THROUGH, 1.18);
const TRAVEL_THROUGH_S = travelTimeForDistance(DIST_THROUGH, 1.0);

function isNightAt(worldSeconds) {
  const tod = secondsIntoDay(worldSeconds);
  return tod >= NIGHT_START || tod < NIGHT_END;
}

/**
 * Grund-Standzeit LOCAL (P1) + Nacht (P2) + Langtag (P4) + Morgenkürze (P2).
 * stationIndex: 0 = south, 1 = north, 2 = mine (spur).
 */
function dwellFor(runIndex, stationIndex, startTime) {
  if (stationIndex === 2) {
    const rng = makeRng(hashInt(0x51ee9, runIndex, 99));
    return rngRange(rng, SPUR_DWELL_MIN, SPUR_DWELL_MAX);
  }

  const rng = makeRng(hashInt(0x51ee9, runIndex, stationIndex));
  const long = rng() < DWELL_LONG_CHANCE;
  let d = long
    ? rngRange(rng, 2.0 * 3600, DWELL_MAX)
    : rngRange(rng, DWELL_MIN, 70 * 60);

  if (stationIndex === 0 && runIndex > 0 && runIndex % LANG_EVERY === 0) {
    d = Math.max(d, rngRange(rng, 5 * 3600, 8 * 3600));
  }

  if (isNightAt(startTime)) {
    d *= NIGHT_DWELL_MULT;
  } else if (stationIndex === 0) {
    const tod = secondsIntoDay(startTime);
    if (tod >= NIGHT_END && tod < 8 * 3600) {
      d *= MORNING_DWELL_MULT;
    }
  }

  return d;
}

// ============================================================================
// LOCAL: Umlaufbau
// ============================================================================

/**
 * Ein LOCAL-Umlauf startet und endet in Süd.
 * kind: 'main' = Süd–Nord–Süd, 'spur' = Süd–Mine–Süd.
 */
function buildLocalRun(runIndex, startTime) {
  const spur = isSpurRun(runIndex);
  const legs = [];
  let t = startTime;

  const southDwell = dwellFor(runIndex, 0, t);
  legs.push({
    kind: 'stand', station: 'south', branch: BRANCH_MAIN,
    t0: t, t1: t + southDwell, s: SOUTH_STAND_S,
  });
  t += southDwell;

  if (spur) {
    const tJunc1 = travelTimeForDistance(DIST_SOUTH_TO_JUNC, 1.0);
    legs.push({
      kind: 'move', direction: +1, branch: BRANCH_MAIN,
      t0: t, t1: t + tJunc1,
      s0: SOUTH_STAND_S, s1: JUNCTION_S,
      route: 'to-mine',
    });
    t += tJunc1;

    const tMine = travelTimeForDistance(DIST_JUNC_TO_MINE, 1.12);
    legs.push({
      kind: 'move', direction: +1, branch: BRANCH_MINE,
      t0: t, t1: t + tMine,
      s0: 0, s1: MINE_STAND_S,
      route: 'to-mine',
    });
    t += tMine;

    const mineDwell = dwellFor(runIndex, 2, t);
    legs.push({
      kind: 'stand', station: 'mine', branch: BRANCH_MINE,
      t0: t, t1: t + mineDwell, s: MINE_STAND_S,
    });
    t += mineDwell;

    const tBack = travelTimeForDistance(DIST_JUNC_TO_MINE, 1.0);
    legs.push({
      kind: 'move', direction: -1, branch: BRANCH_MINE,
      t0: t, t1: t + tBack,
      s0: MINE_STAND_S, s1: 0,
      route: 'from-mine',
    });
    t += tBack;

    const tSouth = travelTimeForDistance(DIST_SOUTH_TO_JUNC, 1.0);
    legs.push({
      kind: 'move', direction: -1, branch: BRANCH_MAIN,
      t0: t, t1: t + tSouth,
      s0: JUNCTION_S, s1: SOUTH_STAND_S,
      route: 'from-mine',
    });
    t += tSouth;
  } else {
    const northDwell = dwellFor(runIndex, 1, t + TRAVEL_NORTH);

    legs.push({
      kind: 'move', direction: +1, branch: BRANCH_MAIN,
      t0: t, t1: t + TRAVEL_NORTH,
      s0: SOUTH_STAND_S, s1: NORTH_STAND_S,
      route: 'main',
    });
    t += TRAVEL_NORTH;

    legs.push({
      kind: 'stand', station: 'north', branch: BRANCH_MAIN,
      t0: t, t1: t + northDwell, s: NORTH_STAND_S,
    });
    t += northDwell;

    legs.push({
      kind: 'move', direction: -1, branch: BRANCH_MAIN,
      t0: t, t1: t + TRAVEL_SOUTH,
      s0: NORTH_STAND_S, s1: SOUTH_STAND_S,
      route: 'main',
    });
    t += TRAVEL_SOUTH;
  }

  return {
    index: runIndex,
    kind: spur ? 'spur' : 'main',
    trainId: TRAIN_LOCAL,
    t0: startTime,
    t1: t,
    legs,
  };
}

const localRunCache = [];
let localCacheEndTime = 0;

function ensureLocalCache(untilWorldSeconds) {
  if (localRunCache.length === 0) {
    localRunCache.push(buildLocalRun(0, 0));
    localCacheEndTime = localRunCache[0].t1;
  }
  let guard = 0;
  while (localCacheEndTime <= untilWorldSeconds + 3600 && guard++ < 10000) {
    const next = buildLocalRun(localRunCache.length, localCacheEndTime);
    localRunCache.push(next);
    localCacheEndTime = next.t1;
    if (localRunCache.length > 4096) localRunCache.splice(0, 2048);
  }
}

/** LOCAL-Abschnitt zur Weltzeit. */
function localLegAt(worldSeconds) {
  const t = Math.max(0, worldSeconds);
  ensureLocalCache(t);
  for (let i = localRunCache.length - 1; i >= 0; i--) {
    const run = localRunCache[i];
    if (t >= run.t0 && t <= run.t1) {
      for (const leg of run.legs) {
        if (t >= leg.t0 && t <= leg.t1) return { run, leg };
      }
      return { run, leg: run.legs[run.legs.length - 1] };
    }
  }
  return { run: localRunCache[0], leg: localRunCache[0].legs[0] };
}

function stateFromLeg(run, leg, worldSeconds, trainId) {
  const branch = leg.branch ?? BRANCH_MAIN;
  const meta = TRAINS.find((tr) => tr.id === trainId) ?? TRAINS[0];

  if (leg.kind === 'stand') {
    return {
      id: trainId,
      role: meta.role,
      boardable: meta.boardable,
      length: meta.length,
      headS: leg.s,
      speed: 0,
      direction: 0,
      state: 'standing',
      station: leg.station,
      branch,
      runKind: run.kind,
      legEnd: leg.t1,
      legStart: leg.t0,
    };
  }

  const dur = leg.t1 - leg.t0;
  const u = Math.min(1, Math.max(0, (worldSeconds - leg.t0) / dur));
  const eased = easeTrapezoid(u);
  const headS = leg.s0 + (leg.s1 - leg.s0) * eased;
  const dEased = dEaseTrapezoid(u) / dur;
  const speed = Math.abs((leg.s1 - leg.s0) * dEased);
  return {
    id: trainId,
    role: meta.role,
    boardable: meta.boardable,
    length: meta.length,
    headS,
    speed,
    direction: leg.direction,
    state: 'moving',
    station: null,
    branch,
    runKind: run.kind,
    route: leg.route ?? 'main',
    legEnd: leg.t1,
    legStart: leg.t0,
  };
}

function localStateAt(worldSeconds) {
  const { run, leg } = localLegAt(worldSeconds);
  return stateFromLeg(run, leg, worldSeconds, TRAIN_LOCAL);
}

// ============================================================================
// Kollision / Belegung
// ============================================================================

/** Belegtes s-Intervall [lo, hi] der Zugspitze (Lok am hohen-s Ende). */
function occupyInterval(st) {
  const len = st.length ?? LOCAL_TRAIN_LENGTH;
  return { lo: st.headS - len, hi: st.headS, branch: st.branch ?? BRANCH_MAIN };
}

/**
 * Steht der Zug auf einem Ausweich-/Nebengleis (1D-Modell: s belegt, aber
 * physisch parallel zur Durchfahrt)? THROUGH nutzt Süd-Nebengleis und den
 * Mittelhalt als Begegnungsabschnitt — LOCAL fährt auf dem durchgehenden
 * Hauptgleis vorbei.
 */
function onPassingSiding(st) {
  if (!st || st.state !== 'standing') return false;
  if (st.station === 'hold') return true;
  // THROUGH am Südhof-Nebengleis
  if (st.id === TRAIN_THROUGH && st.station === 'south') return true;
  if (
    st.station === 'south'
    && Math.abs(st.headS - SOUTH_THROUGH_S) < 40
    && (st.role === 'through' || st.id === TRAIN_THROUGH)
  ) return true;
  return false;
}

/**
 * true, wenn zwei Zustände auf derselben Strecke unzulässig nah sind.
 * Nebengleis-Halt: parallel, Durchfahrt auf main erlaubt.
 * Beide stehend im selben Hof: Kopfabstand muss reichen.
 */
export function trainsConflict(a, b) {
  if (!a || !b) return false;
  const ba = a.branch ?? BRANCH_MAIN;
  const bb = b.branch ?? BRANCH_MAIN;
  if (ba !== bb) return false;

  // Einer (oder beide) auf dem Ausweichgleis stehend: kein 1D-Konflikt mit
  // dem durchfahrenden Zug. Zwei stehende auf demselben Ausweich: Abstand.
  const aSide = onPassingSiding(a);
  const bSide = onPassingSiding(b);
  if (aSide || bSide) {
    if (a.state === 'standing' && b.state === 'standing') {
      // Beide im Hof stehend (LOCAL am Stand, THROUGH am Nebengleis).
      if (a.station === 'south' && b.station === 'south') {
        return Math.abs(a.headS - b.headS) < MIN_YARD_HEAD_SEP;
      }
      // Zwei am selben Hold-Punkt — nicht vorgesehen, hart trennen.
      if (aSide && bSide) {
        return Math.abs(a.headS - b.headS) < MIN_YARD_HEAD_SEP;
      }
    }
    // Stehend auf Siding + bewegter auf main = Begegnung am Ausweich, OK.
    if (aSide !== bSide) return false;
  }

  // Beide stehen im selben Hof → Ausweichgleis, solange Köpfe getrennt.
  if (
    a.state === 'standing' && b.state === 'standing'
    && a.station && a.station === b.station
  ) {
    return Math.abs(a.headS - b.headS) < MIN_YARD_HEAD_SEP;
  }

  const A = occupyInterval(a);
  const B = occupyInterval(b);
  if (A.hi + MIN_OPEN_GAP_M < B.lo) return false;
  if (B.hi + MIN_OPEN_GAP_M < A.lo) return false;
  return true;
}

/**
 * Stationslabel für THROUGH-Standplatz (Süd-Hof vs. Mittelhalt).
 * Mittelhalt gilt nicht als 'north'-Bahnhof — er ist ein Begegnungsabschnitt.
 */
function throughStationForS(s) {
  if (Math.abs(s - SOUTH_THROUGH_S) < 50) return 'south';
  return 'hold'; // Mittelhalt / Ausweich
}

/**
 * Würde THROUGH auf dem Trip s0→s1 im Intervall [t0,t1] mit LOCAL kollidieren?
 * Nur physische Nähe (trainsConflict) — kein pauschaler Korridor-Block, damit
 * gleichgerichtete Fahrten mit Abstand erlaubt sind.
 */
function throughTripConflicts(t0, t1, s0, s1) {
  const dur = t1 - t0;
  if (dur <= 0) return true;
  const steps = Math.max(12, Math.ceil(dur / 15));
  for (let i = 0; i <= steps; i++) {
    const t = t0 + (dur * i) / steps;
    const u = i / steps;
    const eased = easeTrapezoid(u);
    const headS = s0 + (s1 - s0) * eased;
    const thr = {
      headS,
      branch: BRANCH_MAIN,
      state: 'moving',
      station: null,
      length: THROUGH_TRAIN_LENGTH,
    };
    const loc = localStateAt(t);
    if (trainsConflict(loc, thr)) return true;
  }
  // Ankunft: Standplatz am Ziel darf nicht mit LOCAL kollidieren.
  const arrive = {
    headS: s1,
    branch: BRANCH_MAIN,
    state: 'standing',
    station: throughStationForS(s1),
    length: THROUGH_TRAIN_LENGTH,
  };
  if (trainsConflict(localStateAt(t1), arrive)) return true;
  return false;
}

function findClearThroughTrip(fromTime, s0, s1, travelWorld) {
  // Suche deterministisch in 30-s-Schritten; springt bei belegtem Korridor
  // zum nächsten LOCAL-Leg-Ende, falls bekannt.
  let t = fromTime;
  const limit = fromTime + GAME_SECONDS_PER_DAY * 4;
  let guard = 0;
  while (t < limit && guard++ < 20000) {
    const t1 = t + travelWorld;
    if (!throughTripConflicts(t, t1, s0, s1)) {
      return { t0: t, t1 };
    }
    // Blockiert: zum Ende des aktuellen LOCAL-Legs springen oder 30 s.
    const { leg } = localLegAt(t);
    const jump = Math.max(30, Math.min(300, (leg.t1 - t) + 1));
    t += jump;
  }
  // Fallback: trotzdem planen (sollte praktisch nicht vorkommen).
  return { t0: t, t1: t + travelWorld };
}

// ============================================================================
// THROUGH: Umlaufbau (Gegentakt, blockbasiert)
// ============================================================================

/**
 * THROUGH startet am Mittelhalt (t=0): LOCAL steht in Süd — ehrlicher
 * Start ohne doppelte Belegung. Sobald ein freies Fenster existiert, fährt
 * er in den Südhof (sichtbares Ereignis) und hält dort spürbar, bis die
 * Trasse ihn wieder nach draußen zwingt.
 */
function buildThroughRunsUntil(untilWorldSeconds) {
  const runs = [];
  let t = 0;
  let atSouth = false;
  let runIndex = 0;
  const rng = makeRng(hashInt(0x7a401, 0, 1));

  // Initial-Halt am Mittelhalt — gibt LOCAL den Südhof allein (Spawn).
  {
    const d0 = rngRange(rng, THROUGH_NORTH_DWELL_MIN, THROUGH_NORTH_DWELL_MAX);
    runs.push({
      index: runIndex++,
      kind: 'through',
      trainId: TRAIN_THROUGH,
      t0: 0,
      t1: d0,
      legs: [{
        kind: 'stand', station: 'hold', branch: BRANCH_MAIN,
        t0: 0, t1: d0, s: NORTH_THROUGH_S,
      }],
    });
    t = d0;
    atSouth = false;
  }

  let guard = 0;
  while (t < untilWorldSeconds + 7200 && guard++ < 8000) {
    const s0 = atSouth ? SOUTH_THROUGH_S : NORTH_THROUGH_S;
    const s1 = atSouth ? NORTH_THROUGH_S : SOUTH_THROUGH_S;
    const travel = atSouth ? TRAVEL_THROUGH_N : TRAVEL_THROUGH_S;
    const dir = atSouth ? +1 : -1;

    const trip = findClearThroughTrip(t, s0, s1, travel);
    const legs = [];
    if (trip.t0 > t + 1e-6) {
      legs.push({
        kind: 'stand',
        station: throughStationForS(s0),
        branch: BRANCH_MAIN,
        t0: t, t1: trip.t0, s: s0,
      });
    }
    legs.push({
      kind: 'move',
      direction: dir,
      branch: BRANCH_MAIN,
      t0: trip.t0, t1: trip.t1,
      s0, s1,
      route: 'through',
    });

    // Halt am Ziel. atSouth = Startort; Ziel-Süd ⇒ !atSouth → längerer Südhalt
    // (Beobachtungsgegenstand). Mittelhalt kürzer (Warten auf freie Trasse).
    const dwellRng = makeRng(hashInt(0x7a401, runIndex, atSouth ? 2 : 3));
    let dwell = atSouth
      ? rngRange(dwellRng, THROUGH_NORTH_DWELL_MIN, THROUGH_NORTH_DWELL_MAX)
      : rngRange(dwellRng, THROUGH_SOUTH_DWELL_MIN, THROUGH_SOUTH_DWELL_MAX);
    if (isNightAt(trip.t1)) dwell *= 1.2;

    // Standzeit ggf. kürzen, wenn LOCAL den Standplatz bald streift:
    // wir samplen vorausschauend und beenden den Halt bei drohendem Konflikt.
    const standS = s1;
    const standStation = throughStationForS(s1);
    let standEnd = trip.t1 + dwell;
    const probeStep = 60;
    for (let tp = trip.t1 + probeStep; tp < standEnd; tp += probeStep) {
      const hold = {
        headS: standS,
        branch: BRANCH_MAIN,
        state: 'standing',
        station: standStation,
        length: THROUGH_TRAIN_LENGTH,
      };
      if (trainsConflict(localStateAt(tp), hold)) {
        standEnd = Math.max(trip.t1 + 90, tp - probeStep);
        break;
      }
    }

    legs.push({
      kind: 'stand', station: standStation, branch: BRANCH_MAIN,
      t0: trip.t1, t1: standEnd, s: standS,
    });

    const tRun0 = legs[0].t0;
    const tRun1 = legs[legs.length - 1].t1;
    runs.push({
      index: runIndex++,
      kind: 'through',
      trainId: TRAIN_THROUGH,
      t0: tRun0,
      t1: tRun1,
      legs,
    });

    t = tRun1;
    atSouth = !atSouth;
  }
  return runs;
}

const throughRunCache = [];
let throughCacheUntil = -1;

function ensureThroughCache(untilWorldSeconds) {
  ensureLocalCache(untilWorldSeconds + 7200);
  if (throughCacheUntil >= untilWorldSeconds + 3600 && throughRunCache.length > 0) {
    return;
  }
  // Neu aufbauen bis weit genug — deterministisch identisch bei gleichem until.
  const need = untilWorldSeconds + GAME_SECONDS_PER_DAY;
  const rebuilt = buildThroughRunsUntil(need);
  throughRunCache.length = 0;
  for (const r of rebuilt) throughRunCache.push(r);
  throughCacheUntil = need;
}

function throughLegAt(worldSeconds) {
  const t = Math.max(0, worldSeconds);
  ensureThroughCache(t);
  for (let i = throughRunCache.length - 1; i >= 0; i--) {
    const run = throughRunCache[i];
    if (t >= run.t0 && t <= run.t1) {
      for (const leg of run.legs) {
        if (t >= leg.t0 && t <= leg.t1) return { run, leg };
      }
      return { run, leg: run.legs[run.legs.length - 1] };
    }
  }
  // Vor erstem Leg / Lücke: am Nord-Ausweich stehend.
  return {
    run: throughRunCache[0],
    leg: throughRunCache[0]?.legs[0] ?? {
      kind: 'stand', station: 'north', branch: BRANCH_MAIN,
      t0: 0, t1: 1, s: NORTH_THROUGH_S,
    },
  };
}

function throughStateAt(worldSeconds) {
  const { run, leg } = throughLegAt(worldSeconds);
  return stateFromLeg(run, leg, worldSeconds, TRAIN_THROUGH);
}

// ============================================================================
// Öffentliche API
// ============================================================================

/** Aktuellen Abschnitt (leg) des LOCAL zur Weltzeit finden. */
export function legAt(worldSeconds) {
  return localLegAt(worldSeconds);
}

/**
 * Position/Geschwindigkeit der LOCAL-Zugspitze zur Weltzeit.
 * Abwärtskompatibel: bestehender Client (train.js) spricht genau diesen Zug.
 */
export function trainStateAt(worldSeconds) {
  const st = localStateAt(worldSeconds);
  // Alte Felder ohne id/role — Tests vergleichen deepEqual; zusätzliche
  // Felder sind ok, solange die Kernfelder stabil bleiben.
  return st;
}

/** Zustand eines bestimmten Zuges. */
export function trainStateById(trainId, worldSeconds) {
  if (trainId === TRAIN_THROUGH) return throughStateAt(worldSeconds);
  return localStateAt(worldSeconds);
}

/** Alle Züge zur Weltzeit (für Multi-Zug-Rendering und Audio). */
export function allTrainsAt(worldSeconds) {
  return [
    localStateAt(worldSeconds),
    throughStateAt(worldSeconds),
  ];
}

// Trapezprofil normiert auf [0,1] mit Anfahr-/Bremsanteil.
const RAMP = 0.16;
function easeTrapezoid(u) {
  const peak = 1 / (1 - RAMP);
  if (u < RAMP) return 0.5 * peak * (u * u) / RAMP;
  if (u > 1 - RAMP) {
    const v = 1 - u;
    return 1 - 0.5 * peak * (v * v) / RAMP;
  }
  return 0.5 * peak * RAMP + peak * (u - RAMP);
}
function dEaseTrapezoid(u) {
  const peak = 1 / (1 - RAMP);
  if (u < RAMP) return peak * u / RAMP;
  if (u > 1 - RAMP) return peak * (1 - u) / RAMP;
  return peak;
}

/**
 * Nächste Abfahrt LOCAL nach worldSeconds — die Information, die Spieler
 * eigentlich herausfinden wollen. Nur intern/Debug, nie im Spieler-UI.
 */
export function nextDeparture(worldSeconds) {
  ensureLocalCache(worldSeconds + GAME_SECONDS_PER_DAY * 3);
  for (const run of localRunCache) {
    for (const leg of run.legs) {
      if (leg.kind === 'move' && leg.t0 >= worldSeconds) {
        let from = 'south';
        if (leg.branch === BRANCH_MINE && leg.direction < 0) from = 'mine';
        else if (leg.s0 === NORTH_STAND_S) from = 'north';
        else if (leg.s0 === MINE_STAND_S) from = 'mine';
        else if (leg.s0 === JUNCTION_S && leg.direction < 0) from = 'junction';
        else if (leg.s0 === 0 && leg.branch === BRANCH_MINE) from = 'junction';
        return {
          at: leg.t0,
          direction: leg.direction,
          from,
          branch: leg.branch,
          runKind: run.kind,
          trainId: TRAIN_LOCAL,
        };
      }
    }
  }
  return null;
}

/**
 * Nächste Abfahrt eines beliebigen Zuges ab worldSeconds (für Beobachtung).
 */
export function nextAnyDeparture(worldSeconds) {
  ensureLocalCache(worldSeconds + GAME_SECONDS_PER_DAY);
  ensureThroughCache(worldSeconds + GAME_SECONDS_PER_DAY);
  let best = null;
  for (const run of localRunCache) {
    for (const leg of run.legs) {
      if (leg.kind === 'move' && leg.t0 >= worldSeconds) {
        const cand = {
          at: leg.t0, direction: leg.direction, trainId: TRAIN_LOCAL,
          from: leg.s0 === NORTH_STAND_S ? 'north' : 'south',
        };
        if (!best || cand.at < best.at) best = cand;
        break;
      }
    }
  }
  for (const run of throughRunCache) {
    for (const leg of run.legs) {
      if (leg.kind === 'move' && leg.t0 >= worldSeconds) {
        const cand = {
          at: leg.t0, direction: leg.direction, trainId: TRAIN_THROUGH,
          from: leg.s0 === NORTH_THROUGH_S ? 'north' : 'south',
        };
        if (!best || cand.at < best.at) best = cand;
        break;
      }
    }
  }
  return best;
}

/** Fahrplan als Tabelle für Debug/Tests (LOCAL, abwärtskompatibel). */
export function scheduleTable(fromWorldSeconds, count = 8) {
  return scheduleTableForTrain(TRAIN_LOCAL, fromWorldSeconds, count);
}

/** Fahrplanzeilen eines konkreten Zuges; nur Debug/Tests, niemals HUD. */
export function scheduleTableForTrain(trainId, fromWorldSeconds, count = 8) {
  const until = fromWorldSeconds + count * 12 * 3600;
  if (trainId === TRAIN_THROUGH) ensureThroughCache(until);
  else ensureLocalCache(until);
  const cache = trainId === TRAIN_THROUGH ? throughRunCache : localRunCache;
  const rows = [];
  for (const run of cache) {
    for (const leg of run.legs) {
      if (leg.t1 < fromWorldSeconds) continue;
      rows.push({
        ...leg,
        run: run.index,
        runKind: run.kind ?? (trainId === TRAIN_THROUGH ? 'through' : 'main'),
        trainId,
      });
      if (rows.length >= count * 3) return rows;
    }
  }
  return rows;
}

/** Beide Fahrpläne chronologisch zusammengeführt — ausschließlich F3/Diagnose. */
export function scheduleTableAll(fromWorldSeconds, countPerTrain = 4) {
  const rows = [
    ...scheduleTableForTrain(TRAIN_LOCAL, fromWorldSeconds, countPerTrain),
    ...scheduleTableForTrain(TRAIN_THROUGH, fromWorldSeconds, countPerTrain),
  ];
  rows.sort((a, b) => a.t0 - b.t0 || String(a.trainId).localeCompare(String(b.trainId)));
  return rows;
}

/**
 * Liefert alle LOCAL-Umläufe im Zeitfenster (für Analysen / check-network).
 */
export function runsInRange(fromWorldSeconds, toWorldSeconds) {
  ensureLocalCache(toWorldSeconds);
  return localRunCache.filter((r) => r.t1 >= fromWorldSeconds && r.t0 <= toWorldSeconds);
}

/** THROUGH-Umläufe im Fenster (Tests / Traffic-Check). */
export function throughRunsInRange(fromWorldSeconds, toWorldSeconds) {
  ensureThroughCache(toWorldSeconds);
  return throughRunCache.filter((r) => r.t1 >= fromWorldSeconds && r.t0 <= toWorldSeconds);
}

// ============================================================================
// Beobachtung: Anzeichen in der Welt (kein UI)
// ============================================================================

const _southPos = trackPoint(SOUTH.s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
const _northPos = trackPoint(NORTH.s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);

/** Weltposition der Zugspitze. */
export function trainWorldPos(st, out = { x: 0, y: 0, z: 0 }) {
  return trackPoint(st.headS, out, st.branch ?? BRANCH_MAIN);
}

/** Euklidische Distanz Zugspitze → Punkt. */
export function trainDistanceTo(st, x, y, z) {
  const p = trainWorldPos(st);
  return Math.hypot(p.x - x, p.y - y, p.z - z);
}

export function distanceToStation(st, stationId = 'south') {
  const anchor = stationId === 'north' ? _northPos : _southPos;
  return trainDistanceTo(st, anchor.x, anchor.y, anchor.z);
}

/**
 * Nächster Zug zu einem Weltpunkt (für Audio-Pegel, Sicht).
 * @returns {{ train: object, distance: number } | null}
 */
export function nearestTrainTo(worldSeconds, x, y, z) {
  const trains = allTrainsAt(worldSeconds);
  let best = null;
  for (const tr of trains) {
    const d = trainDistanceTo(tr, x, y, z);
    if (!best || d < best.distance) best = { train: tr, distance: d };
  }
  return best;
}

/**
 * Hof-Aktivität / Anzeichen (Konzept §11: Lichter, Leute, Rangieren).
 * Reine Weltschicht — workers.js und audio.js lesen das.
 *
 * phase:
 *   'idle'          — Alltagspatrouille
 *   'pre-depart'    — LOCAL fährt bald ab → Arbeiter an Gleispositionen
 *   'arrival-soon'  — irgendein Zug nähert sich dem Hof
 *   'train-present' — Zug steht/fährt im Hofbereich
 */
export function yardActivityAt(worldSeconds, stationId = 'south') {
  const t = Math.max(0, worldSeconds);
  const standS = stationId === 'north' ? NORTH_STAND_S : SOUTH_STAND_S;
  const yard = stationId === 'north' ? NORTH : SOUTH;
  const trains = allTrainsAt(t);

  let nearestDist = Infinity;
  let nearest = null;
  let present = false;
  let approaching = null;

  for (const tr of trains) {
    const d = distanceToStation(tr, stationId);
    if (d < nearestDist) {
      nearestDist = d;
      nearest = tr;
    }
    const inYard = (tr.branch ?? BRANCH_MAIN) === BRANCH_MAIN
      && tr.headS >= yard.yardStart - 30
      && tr.headS <= yard.yardEnd + 30;
    if (inYard) present = true;

    // Nähert sich dem Hof auf main?
    if (
      tr.state === 'moving'
      && (tr.branch ?? BRANCH_MAIN) === BRANCH_MAIN
      && d < TRAIN_RUMBLE_RANGE_M
      && d > 80
    ) {
      const towardSouth = tr.direction < 0 && stationId === 'south';
      const towardNorth = tr.direction > 0 && stationId === 'north';
      // Auch: fährt aus dem Korridor in den Hof (s nähert sich standS).
      const closing = Math.abs(tr.headS - standS) < Math.abs(
        (tr.headS - tr.direction * 50) - standS,
      );
      if ((towardSouth || towardNorth || (closing && d < TRAIN_SIGHT_M * 2))) {
        if (!approaching || d < approaching.distance) {
          approaching = { train: tr, distance: d, direction: tr.direction };
        }
      }
    }
  }

  // LOCAL-Abfahrt von diesem Hof bald?
  let preDepart = false;
  let departIn = null;
  const loc = trains.find((tr) => tr.id === TRAIN_LOCAL) ?? localStateAt(t);
  if (loc.state === 'standing' && loc.station === stationId) {
    departIn = loc.legEnd - t;
    if (departIn >= 0 && departIn <= PRE_DEPART_WORLD_S) {
      preDepart = true;
    }
  }

  let phase = 'idle';
  if (preDepart) phase = 'pre-depart';
  else if (present) phase = 'train-present';
  else if (approaching) phase = 'arrival-soon';

  return {
    stationId,
    phase,
    preDepart,
    departInWorld: departIn,
    present,
    approaching,
    nearestTrainId: nearest?.id ?? null,
    nearestDist,
    // 0..1 — wie „wach" der Hof wirkt (Arbeiter, Lärm).
    alert: preDepart ? 1
      : approaching ? Math.max(0.35, 1 - approaching.distance / TRAIN_RUMBLE_RANGE_M)
        : present ? 0.55
          : 0.15,
  };
}

/**
 * Hör-Pegel für Annäherung (Konzept: Grollen → Schienensingen → Horn).
 * Rein, deterministisch aus Distanz + Zustand — audio.js moduliert damit.
 *
 * @returns {{ rumble: number, sing: number, hornOk: boolean, distance: number, train: object|null }}
 */
export function approachCueAt(worldSeconds, listenerX, listenerY, listenerZ) {
  const n = nearestTrainTo(worldSeconds, listenerX, listenerY, listenerZ);
  if (!n) {
    return { rumble: 0, sing: 0, hornOk: false, distance: Infinity, train: null };
  }
  const d = n.distance;
  const tr = n.train;
  // Grollen: ab RUMBLE_RANGE, peakt wenn nah und bewegt.
  const moving = tr.state === 'moving' && tr.speed > 0.05;
  let rumble = 0;
  if (d < TRAIN_RUMBLE_RANGE_M) {
    const prox = 1 - d / TRAIN_RUMBLE_RANGE_M;
    rumble = prox * prox * (moving ? 1 : 0.25);
  }
  // Schienensingen: enger, nur bei Fahrt.
  let sing = 0;
  if (moving && d < TRAIN_SING_RANGE_M) {
    sing = (1 - d / TRAIN_SING_RANGE_M) ** 1.4;
  }
  const hornOk = d <= TRAIN_HORN_RANGE_M;
  return { rumble, sing, hornOk, distance: d, train: tr };
}

/**
 * Darf an listener-Position ein Horn dieses Zuges hörbar sein?
 * Behebt die Lüge: Horn nur bei realer Nähe (Konzept §12.1 / Nutzerreport).
 */
export function hornAudibleAt(trainState, listenerX, listenerY, listenerZ) {
  if (!trainState) return false;
  const d = trainDistanceTo(trainState, listenerX, listenerY, listenerZ);
  return d <= TRAIN_HORN_RANGE_M;
}

/**
 * Mindestabstand (Enden-Lücke oder Hof-Kopfabstand) zwischen beiden Zügen.
 * Positiv = ok, negativ = Überlappung. Nebengleis-Begegnung → großer Wert.
 */
export function trainSeparationAt(worldSeconds) {
  const [a, b] = allTrainsAt(worldSeconds);
  if ((a.branch ?? BRANCH_MAIN) !== (b.branch ?? BRANCH_MAIN)) {
    return Infinity;
  }
  // Parallel auf Ausweich: kein Konflikt in 1D.
  if (onPassingSiding(a) || onPassingSiding(b)) {
    if (a.state === 'standing' && b.state === 'standing'
      && a.station === 'south' && b.station === 'south') {
      return Math.abs(a.headS - b.headS) - MIN_YARD_HEAD_SEP;
    }
    if (onPassingSiding(a) !== onPassingSiding(b)) return 500;
  }
  if (
    a.state === 'standing' && b.state === 'standing'
    && a.station && a.station === b.station
  ) {
    return Math.abs(a.headS - b.headS) - MIN_YARD_HEAD_SEP;
  }
  const A = occupyInterval(a);
  const B = occupyInterval(b);
  if (A.hi < B.lo) return B.lo - A.hi;
  if (B.hi < A.lo) return A.lo - B.hi;
  return Math.min(A.hi, B.hi) - Math.max(A.lo, B.lo) > 0
    ? -(Math.min(A.hi, B.hi) - Math.max(A.lo, B.lo))
    : 0;
}

export const SCHEDULE_INFO = Object.freeze({
  travelNorth: TRAVEL_NORTH,
  travelSouth: TRAVEL_SOUTH,
  travelToMine: TRAVEL_TO_MINE,
  travelFromMine: TRAVEL_FROM_MINE,
  travelThroughN: TRAVEL_THROUGH_N,
  travelThroughS: TRAVEL_THROUGH_S,
  cruiseSpeed: CRUISE_SPEED,
  cruiseSpeedReal: CRUISE_SPEED_REAL,
  trackLength: TRACK_LENGTH,
  mineTrackLength: MINE_TRACK_LENGTH,
  spurEvery: SPUR_EVERY,
  spurPhase: SPUR_PHASE,
  langEvery: LANG_EVERY,
  // Dokumentierte Beobachtungsdauer LOCAL (siehe Dateikopf / check-network.js).
  observeGameDays: 9,
  observeRealHours: 18,
  trainCount: TRAIN_IDS.length,
  trainIds: TRAIN_IDS.slice(),
  sightM: TRAIN_SIGHT_M,
  hornRangeM: TRAIN_HORN_RANGE_M,
  // Ziel-Median-Wartezeit am Südhof auf irgendeinen Zug in Sicht (Echtzeitmin).
  // Begründung: Prototyp-1-Fenster ist 15 Min — Median deutlich darunter, damit
  // „verlässlich etwas passiert", ohne Zoo (P75 darf höher liegen).
  targetMedianWaitRealMin: 8,
});

/**
 * Sichtbare Geschwindigkeit in Echtzeit-m/s (für Sound, Partikel, Kamera).
 */
export function realSpeedFrom(worldSpeed) {
  return worldSpeed * TIME_SCALE;
}

/** Max. erlaubte Bogenlänge der aktuellen Strecke des Zuges. */
export function branchLengthOf(branch) {
  return trackLength(branch ?? BRANCH_MAIN);
}

/**
 * Ehrlicher Start: Season-Epoche t=0 hat LOCAL in Süd stehend.
 * Kein pro-Spieler-Zeitcheat (Konzept §5: eine globale Weltzeit).
 * Die Verkehrsdichte (zweiter Zug + leicht verdichteter Pendler) ist die
 * ehrliche Antwort auf „ich steige in die Leere" — nicht ein gefälschter
 * Spawn-Zeitpunkt.
 */
export const HONEST_SPAWN = Object.freeze({
  localAtSouthAtEpoch: true,
  epochWorldSeconds: 0,
  note: 'Globale Weltzeit unangetastet; Dichte statt Zeitmanipulation.',
});
